import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";

import { BULK_INVITE_WITH_USER_IDS } from "../gql/mutations/event";
import { RECORD_INVITES } from "../gql/mutations/outreach";
import { EVENT_MEMBER_MATCHES } from "../gql/queries/eventMatching";
import { SHARE_EVENT_TO_EMAILS } from "../gql/queries/shareEvent";
import { ZONE_COLOURS } from "../helpers/const";
import { Spacing } from "../types/enum";
import type { ZoneLabel } from "../types/graphql";

/** Best-ranked N are pre-ticked (unless over-contacted). */
const PRESELECT = 25;

const MIN_ENGLISH_OPTIONS = [
  ["", "Any level"],
  ["Intermediate", "Intermediate+"],
  ["Advanced", "Advanced only"],
];

interface Label {
  key: string;
  name: string;
  family: string;
}
interface ShortlistMember {
  userId: string;
  name: string;
  email: string;
  zone?: ZoneLabel | null;
  englishBand: string;
  engagement: string;
  eventsRecent: number;
  eventsAttended: number;
  localHour?: number | null;
  isNewcomer: boolean;
  recentNotifs: number;
  overContacted: boolean;
  connectionScore: number;
  why: string[];
}

const ZoneChip: React.FC<{ zone?: ZoneLabel | null }> = ({ zone }) =>
  zone ? (
    <Chip
      size="small"
      label={zone}
      sx={{ backgroundColor: ZONE_COLOURS.get(zone), color: "#fff", fontWeight: 600 }}
    />
  ) : null;

const hourLabel = (hour?: number | null): string => {
  if (hour === null || hour === undefined) {
    return "—";
  }
  const h = hour % 12 === 0 ? 12 : hour % 12;
  return `${h} ${hour < 12 ? "AM" : "PM"}`;
};

interface Props {
  eventId: string;
  title: string;
  zone?: ZoneLabel | null;
  maxParticipants?: number | null;
  onClose?: () => void;
  /**
   * Bump this to pull the shortlist again — the event's labels decide who is
   * matched, so an open shortlist is stale the moment they are edited.
   */
  refreshToken?: number;
}

/**
 * The member shortlist for one event — the best-matched members (by the event's
 * labels, else by colour zone), with the over-contacted guard, and the Share /
 * Invite actions. Used on the Member Events board and on the event's Invite tab.
 */
const EventInviteShortlist: React.FC<Props> = ({
  eventId,
  title,
  zone,
  maxParticipants,
  onClose,
  refreshToken = 0,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [minEnglish, setMinEnglish] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data, loading, refetch } = useQuery(EVENT_MEMBER_MATCHES, {
    variables: { eventId, limit: 60, minEnglish: minEnglish || null },
    fetchPolicy: "cache-and-network",
  });
  const [shareEvent, { loading: sharing }] = useMutation(SHARE_EVENT_TO_EMAILS);
  const [recordInvites, { loading: recording }] = useMutation(RECORD_INVITES);
  const [inviteMembers, { loading: inviting }] = useMutation(BULK_INVITE_WITH_USER_IDS);
  // Either request in flight disables both buttons, so a double click can't send twice.
  // Invite records to the outreach log after inviting, so that counts as in flight too.
  const busy = sharing || inviting || recording;

  const result = data?.adminEventMemberMatches;
  const members: ShortlistMember[] = useMemo(() => result?.matches ?? [], [result]);
  const zoneMode = result?.matchBasis === "zone";

  // Labels were edited while the shortlist was open: the recommendations below
  // no longer come from the event's current labels, so ask for them again.
  useEffect(() => {
    if (refreshToken > 0) refetch();
  }, [refreshToken, refetch]);

  // Pre-tick the best-ranked N, never auto-ticking over-contacted members.
  // The refetch above lands here too: new recommendations replace the old
  // ticks, so a member matched on a label that has since been removed can
  // never stay selected.
  useEffect(() => {
    setSelected(
      new Set(members.filter((m, i) => i < PRESELECT && !m.overContacted).map((m) => m.email)),
    );
  }, [members]);

  const autoUnticked = members.filter((m, i) => i < PRESELECT && m.overContacted).length;

  const toggle = (email: string) => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(email)) {
        next.delete(email);
      } else {
        next.add(email);
      }
      return next;
    });
  };

  const allChecked = members.length > 0 && members.every((m) => selected.has(m.email));

  const onShare = async () => {
    const emails = members.filter((m) => selected.has(m.email)).map((m) => m.email);
    if (emails.length === 0) {
      enqueueSnackbar("Select at least one member.", { variant: "info" });
      return;
    }
    try {
      const { data: r } = await shareEvent({
        variables: { eventId, emails, source: "SHARE_EVENT" },
      });
      const shared = r?.adminShareEventToEmails?.sharedCount ?? 0;
      enqueueSnackbar(`Shared with ${shared} member(s).`, {
        variant: shared > 0 ? "success" : "warning",
      });
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : "Share failed.", {
        variant: "error",
      });
    }
  };

  const onInvite = async () => {
    const userIds = members.filter((m) => selected.has(m.email)).map((m) => m.userId);
    if (userIds.length === 0) {
      enqueueSnackbar("Select at least one member.", { variant: "info" });
      return;
    }
    try {
      await inviteMembers({ variables: { eventId, input: { userIds } } });
      enqueueSnackbar(`Invited ${userIds.length} member(s).`, { variant: "success" });
      try {
        await recordInvites({ variables: { eventId, userIds, source: "SHARE_EVENT" } });
      } catch {
        /* they are invited either way; the log is a record, not a gate */
      }
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : "Invite failed.", {
        variant: "error",
      });
    }
  };

  const srcNote = zoneMode
    ? "We couldn't label this event, so it's matched by colour zone — members active around this time and how engaged they are, to reach people likely to come and support the host."
    : "Matched to the event's labels by what members told us — their interests, English goals, job and bio.";

  const shownCount = members.length;
  const total = result?.totalMatches ?? 0;
  const excl = result?.excludedCantAccess
    ? ` · ${result.excludedCantAccess} can't access it (skipped)`
    : "";
  const engf = result?.englishFiltered
    ? ` · ${result.englishFiltered} below the English level`
    : "";
  const quiet = result?.quietFiltered
    ? ` · ${result.quietFiltered} in their 11pm–7am (skipped)`
    : "";
  // Says why the shortlist for a welcome event is short, instead of leaving it
  // looking like nobody matched.
  const newcomer = result?.newcomerFiltered
    ? ` · ${result.newcomerFiltered} past their first 3 months (a New Member Welcome isn't for them)`
    : "";

  return (
    <Card variant="outlined">
      <Box p={Spacing.l}>
        <Box display="flex" alignItems="center" gap={Spacing.s} flexWrap="wrap" mb={Spacing.s}>
          <Typography variant="h5" color="textPrimary">
            🌱 Members for {title}
          </Typography>
          <ZoneChip zone={result?.eventZone ?? zone} />
          {result?.isFull ? (
            <Chip
              size="small"
              color="error"
              label={`full · ${result.spotsTaken}/${result.maxParticipants}`}
            />
          ) : (
            <Chip
              size="small"
              variant="outlined"
              label={
                result && result.maxParticipants < 0
                  ? "no limit"
                  : `${result?.spotsTaken ?? 0}/${result?.maxParticipants ?? maxParticipants ?? "—"}`
              }
            />
          )}
          {result?.eventLevel && (
            <Chip
              size="small"
              variant="outlined"
              color="primary"
              label={`${result.eventLevel}-level only`}
            />
          )}
          <Box flexGrow={1} />
          {onClose && (
            <Button size="small" onClick={onClose}>
              ✕ Close
            </Button>
          )}
        </Box>

        <Box display="flex" flexWrap="wrap" gap={0.5} mb={Spacing.s}>
          {result?.labels.length ? (
            result.labels.map((l: Label) => (
              <Chip
                key={l.key}
                label={l.name}
                size="small"
                variant="outlined"
                color={l.family === "skill" ? "secondary" : "primary"}
              />
            ))
          ) : (
            <Typography variant="caption" color="textSecondary">
              no labels
            </Typography>
          )}
        </Box>
        <Typography variant="caption" color="textSecondary" display="block" mb={Spacing.s}>
          {srcNote}
        </Typography>

        {result?.isFull && (
          <Alert severity="warning" sx={{ mb: Spacing.s }}>
            This event is full ({result.spotsTaken}/{result.maxParticipants} spots taken, including
            pending invites). Sharing or inviting more won't add spots — skip unless you're building
            a wait-list.
          </Alert>
        )}

        {autoUnticked > 0 && (
          <Alert severity="warning" sx={{ mb: Spacing.s }}>
            <strong>{autoUnticked}</strong> member(s) were <strong>auto-unticked</strong> — they
            already have <strong>4+</strong> still-upcoming invites/shares. More may overwhelm them;
            tick only if you're sure.
          </Alert>
        )}

        <Box display="flex" alignItems="center" gap={Spacing.m} flexWrap="wrap" mb={Spacing.s}>
          <TextField
            select
            size="small"
            label="Min English"
            value={minEnglish}
            onChange={(e) => setMinEnglish(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            {MIN_ENGLISH_OPTIONS.map(([v, l]) => (
              <MenuItem key={v} value={v}>
                {l}
              </MenuItem>
            ))}
          </TextField>
          <Typography variant="caption" color="textSecondary">
            Showing {shownCount < total ? `top ${shownCount} of ${total}` : `all ${shownCount}`}{" "}
            match(es), best first{excl}
            {engf}
            {quiet}
            {newcomer}.
          </Typography>
        </Box>

        {loading && members.length === 0 ? (
          <Box display="flex" justifyContent="center" p={Spacing.l}>
            <CircularProgress size={24} />
          </Box>
        ) : members.length === 0 ? (
          <Alert severity="info">
            {result?.excludedCantAccess
              ? `The ${result.excludedCantAccess} candidate member(s) can't access this event, so none are shown.`
              : "No members to shortlist for this event."}
          </Alert>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: "55vh" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allChecked}
                        indeterminate={selected.size > 0 && !allChecked}
                        onChange={() =>
                          setSelected(allChecked ? new Set() : new Set(members.map((m) => m.email)))
                        }
                      />
                    </TableCell>
                    <TableCell>Member</TableCell>
                    <TableCell>Zone</TableCell>
                    <TableCell>Local time</TableCell>
                    <TableCell>English</TableCell>
                    <TableCell>Engagement</TableCell>
                    <TableCell>{zoneMode ? "Attended" : "Why suggested"}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {members.map((m, i) => (
                    <TableRow
                      key={m.userId}
                      hover
                      sx={
                        m.overContacted ? { backgroundColor: "rgba(178,58,106,0.06)" } : undefined
                      }
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected.has(m.email)}
                          onChange={() => toggle(m.email)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {i + 1}. {m.name} {m.isNewcomer ? "🆕" : ""}
                        </Typography>
                        {m.overContacted ? (
                          <Typography variant="caption" color="error">
                            ⚠ {m.recentNotifs} upcoming invites/shares
                          </Typography>
                        ) : m.recentNotifs > 0 ? (
                          <Typography variant="caption" color="textSecondary">
                            · {m.recentNotifs} upcoming
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="textSecondary">
                            {m.email}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <ZoneChip zone={m.zone} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{hourLabel(m.localHour)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{m.englishBand}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {m.engagement} · {m.eventsRecent}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {zoneMode ? (
                          <Typography variant="caption" color="textSecondary">
                            {m.eventsAttended} event{m.eventsAttended === 1 ? "" : "s"}
                          </Typography>
                        ) : (
                          <Stack spacing={0.25}>
                            {m.why.map((reason) => (
                              <Typography key={reason} variant="caption" color="textPrimary">
                                • {reason}
                              </Typography>
                            ))}
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box display="flex" gap={Spacing.s} mt={Spacing.m}>
              <Button variant="contained" onClick={onShare} disabled={busy || selected.size === 0}>
                📤 Share with selected ({selected.size})
              </Button>
              <Button variant="outlined" onClick={onInvite} disabled={busy || selected.size === 0}>
                📨 Invite selected
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Card>
  );
};

export default EventInviteShortlist;
